import { ChangeEvent, Dispatch, FC, useRef, useEffect, Fragment, SetStateAction, useState } from 'react'
import { Box, CircularProgress, ClickAwayListener, IconButton, InputAdornment, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { Form, Formik } from 'formik'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from 'react-query'
import isEqual from 'lodash.isequal'
import axios from 'lib/axios'
import theme from 'theme'
import toast from 'react-hot-toast'

/* Utils */
import { schema } from 'ui/pages/library/library.constants'
import { Checked } from '../../CPIDetails'
import { CPIManifest, IFramework, IntegrationInstance } from 'models'
import { hasPermission, hasRole, Permission, Role } from 'lib/utils'
import { useAppDispatch } from 'store/hooks'
import { ModalType, openModal } from 'store/modals/modalSlice'
import { getSlaFullUnit } from 'ui/pages/library/utils'
import { multiplicativeValueCpis } from '../trends-tab/chart-utils'
import { useLibraryDetails } from 'contexts/LibraryDetailsContext'
import {
  getCheckedIntegrationsNames,
  getFormValues,
  getIntegrationIds,
  getManifestUserInput,
  getManifestUserInputSnowflake,
  getSelectedIntegrations,
  handleCustomFwCpi,
  updateCriticalSla
} from './drawer-settings.utils'
import { DevicesTabs } from '../../useLibraryDetailsData'

/* Components */
import { Drawer } from './settingsDrawer.styles'
import { disabledStyles, Button } from 'ui/components/buttons'
import { LibraryTooltipActiveIcon, LibraryTooltipIcon } from 'ui/components/svg'
import { LibraryDetailsInfoTooltip } from 'ui/components/tooltip/LibraryDetailsTooltip'
import ControlledTrendsChartSelectField from 'ui/components/form-components/ControlledTrendsChartSelectField'
import { PermissionTooltip } from 'ui/components/tooltip'
import TextFieldCustomLabel from './components/TextFieldCustomLabel'
import { HotToast } from 'ui/components/toast'
import DataSourceCard from './components/DataSourceCard'

interface Props {
  checked: Checked
  handleChangeInstance: (isChecked: boolean, name: string) => void
  deactivateCpi: () => void
  fetchCpiConfig: (
    currentCpiName: string,
    manifestData: CPIManifest | null,
    userConfiguredIntegrations: IntegrationInstance[],
    cpiSupportedAndUserConfiguredList: IntegrationInstance[]
  ) => void
  fetchAll: (name?: string) => void
  customFramework: IFramework | null
  slaDescriptionTooltipOpen: boolean
  setSlaDescriptionTooltipOpen: Dispatch<SetStateAction<boolean>>
  setChecked: Dispatch<SetStateAction<{ [key: string]: boolean }>>
}

const CPILibrarySettingsDrawer: FC<Props> = ({
  checked,
  handleChangeInstance,
  deactivateCpi,
  fetchCpiConfig,
  fetchAll,
  customFramework,
  slaDescriptionTooltipOpen,
  setSlaDescriptionTooltipOpen,
  setChecked
}) => {
  const {
    setShowMessage,
    setRecalculatingCpiName,
    authUser,
    drawerOpen,
    active,
    cpi,
    fw,
    manifestData,
    cpiConfiguration,
    setCriticalSla,
    setChartData,
    setDrawerOpen,
    defaultValues,
    cpiSupportedAndUserConfiguredList,
    emptyInfoTooltipOpen,
    setEmptyInfoTooltipOpen,
    handleEmptyInfoClick,
    userConfiguredIntegrations,
    setTabValue,
    handleSlaDescriptionTooltipClick,
    notConfiguredInstancesApplicableToCpi,
    isSnowflake,
    deactivateCpiV1,
    setDeactivateCpiV1,
    triggerTrendsRefetch,
    manifestV2,
    setInitialQueryLoaded
  } = useLibraryDetails()
  const wrapperInnerRef = useRef<HTMLDivElement | null>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()
  const [saveDisabled, setSaveDisabled] = useState(true)
  const [isUpdating, setIsUpdating] = useState(false)

  const handleDeactivate = () => {
    if (!manifestData) return

    const callback = () => {
      setChartData(null)
      setDrawerOpen(false)
      deactivateCpi()
      setTabValue(DevicesTabs.About)
      setChecked({})
      setDeactivateCpiV1(false)
      fetchAll()
    }

    const closeback = () => {
      const findItem = cpiSupportedAndUserConfiguredList.find((item) => !item.deleted)

      return dispatch(
        openModal({
          type: ModalType.deleteDeactivation,
          props: {
            integrationName: findItem?.integration_name || '',
            cpiSupportedAndUserConfiguredList,
            manifestData: manifestData,
            callback: () => handleDeactivate(),
            isSnowflake
          }
        })
      )
    }

    dispatch(
      openModal({
        type: ModalType.deactivateCpi,
        props: { cpiName: manifestData.name, callback, closeback, isSnowflake: deactivateCpiV1 ? false : isSnowflake }
      })
    )
  }

  const handleUpdate = async (values: any) => {
    if (!cpi || !manifestData || !active) return

    const formValues = getFormValues(values)

    let previousCheckedIntegrationNames: string[] = []
    const configured = cpiConfiguration?.integrations.map((n) => n.id)
    const userConfiguredIntegrationNames = cpiSupportedAndUserConfiguredList.map((n) => n.integration_name)
    if (active && !!configured) {
      const cpiSupportedAndConfigured = cpiConfiguration?.integrations.filter((n) =>
        userConfiguredIntegrationNames.includes(n.name)
      )
      if (cpiSupportedAndConfigured) {
        previousCheckedIntegrationNames = cpiSupportedAndConfigured.map((n) => n.name)
      }
    }

    const checkedIntegrationsNames = getCheckedIntegrationsNames({ checked })

    if (!checkedIntegrationsNames.length) {
      return handleDeactivate()
    }

    const hasFormValueChanges = !isEqual(defaultValues, formValues)
    const hasIntegrationChanges = !isEqual(previousCheckedIntegrationNames, checkedIntegrationsNames)

    if (!hasFormValueChanges && !hasIntegrationChanges) {
      setSaveDisabled(true)

      return
    }

    const integrationIds = getIntegrationIds({ checkedIntegrationsNames, cpiSupportedAndUserConfiguredList })
    const manifestUserInput = getManifestUserInput({ manifestData, values, cpi })

    const payload = {
      user_input: manifestUserInput,
      integration_ids: integrationIds
    }

    try {
      let payloadSnowflake: any = {}

      if (isSnowflake) {
        const metricManifest = await axios.post('/api/v4/manifests/metrics/filter', {
          manifest: {
            slug: {
              any_: [manifestData.name]
            }
          }
        })

        if (metricManifest.status === 200) {
          const {
            spec: {
              ui: {
                trend_chart: { user_input: userInputSnowflake }
              }
            }
          } = metricManifest.data[0]

          const manifestUserInputSnowflake = getManifestUserInputSnowflake({
            userInputSnowflake,
            formValues
          })

          payloadSnowflake = {
            metric_config: manifestUserInputSnowflake,
            instance_names: integrationIds
          }
        }
      }

      setIsUpdating(true)
      const url = isSnowflake
        ? `/api/v4/metrics/${manifestData.name}/config?update-metric-config-snowflake`
        : `/api/v3/cpis/${manifestData.name}?update-metric-config`
      const updatePayload = isSnowflake ? payloadSnowflake : payload
      const res = await axios.put(url, updatePayload)

      if (res.status === 200) {
        const { data } = res
        updateCriticalSla({ data, manifestData, setCriticalSla })

        await fetchAll(manifestData.name)
        await fetchCpiConfig(
          manifestData.name,
          manifestData,
          userConfiguredIntegrations,
          cpiSupportedAndUserConfiguredList
        )
        triggerTrendsRefetch()
        setInitialQueryLoaded(false) // Trigger refetch of devices/records data

        setShowMessage(true)
        setRecalculatingCpiName(manifestData.name)
        setDrawerOpen(false)
        toast.success(() => (
          <HotToast text="Update Successful" description="Your CPI configuration has been updated." />
        ))
      }
    } catch (e) {
      console.error(e)
      let message = 'Failed updating CPI.'
      const errMessage = (e as any)?.response?.data?.message
      if (errMessage) {
        message = errMessage.includes('Invalid uuid') ? 'Please select at least 1 data source' : errMessage
      }
      toast.error(() => <HotToast text="Error" description={message} />)
    } finally {
      setIsUpdating(false)
    }
  }

  const handleSubmit = async (values: any) => {
    if (!manifestData || !cpi) return
    setIsUpdating(true)

    await handleCustomFwCpi({
      active,
      fw,
      customFramework,
      values,
      manifestData
    })

    const selectedIntegrations = getSelectedIntegrations({ checked, userConfiguredIntegrations, setIsUpdating })
    if (!selectedIntegrations.length) return
    const manifestUserInput = getManifestUserInput({ manifestData, values, cpi })

    try {
      let res: any
      if (manifestV2) {
        const checkedInstanceNames = Object.keys(checked).filter((key) => checked[key])
        const filtered = userConfiguredIntegrations.filter((c) =>
          checkedInstanceNames.includes((c as any).instance_name)
        )
        const payloadInstanceNames = filtered.map((c) => c.instance_name)
        res = await axios.post(`/api/v4/metrics/${manifestData.name}/create?activate-metric`, {
          instance_names: payloadInstanceNames,
          metric_config: manifestUserInput
        })
      } else {
        const payload = {
          user_input: manifestUserInput,
          integration_ids: selectedIntegrations.filter((n) => n !== '')
        }

        res = await axios.post(`/api/v3/cpis/${manifestData.name}?activate-cpi`, payload)
      }

      if (res.status.toString().includes('20')) {
        /* The library page reads its list from the react-query cache. Mark that list stale, so
         * the page shows the activated CPI. */
        queryClient.invalidateQueries(['cpis'])
        navigate('/library')
        setDrawerOpen(false)
        toast.success(() => (
          <HotToast text="CPI Activated" description="You can now see the CPI details in your library." />
        ))
      }
    } catch (e) {
      console.error(e)
      let message = active ? 'Failed updating CPI' : 'Failed activating CPI.'
      if ((e as any).response.data.message && (e as any).response.data.message.includes('Invalid uuid')) {
        message = 'Please select at least 1 data source'
      } else if ((e as any).response.data.message) {
        message = (e as any).response.data.message
      }
      toast.error(() => <HotToast text="Error" description={message} />)
    } finally {
      setIsUpdating(false)
    }
  }

  const renderVisualizationParam = (handleChange: any, errors: any, values: any) => {
    if (!manifestData) return null
    const { user_input: userInput } = manifestData.ui.trend_chart

    if (userInput.visualization_param.identical_section_values) {
      return (
        <>
          <Box className="benchmarks-title-wrapper">
            <Box
              className={`benchmarks-title ${multiplicativeValueCpis.includes(manifestData.name) && 'with-info-icon'}`}
            >
              <Box className="benchmarks-title">
                <Typography className="benchmark-header">
                  {userInput.visualization_param.header}
                  <span>*</span>
                </Typography>
                {userInput.visualization_param.sub_header && (
                  <Typography className="benchmark-subheader">{userInput.visualization_param.sub_header}</Typography>
                )}
              </Box>
              {multiplicativeValueCpis.includes(manifestData.name) && (
                <Box className="benchmarks-title-tooltip-wrapper">
                  <ClickAwayListener onClickAway={() => setSlaDescriptionTooltipOpen(false)}>
                    <div className="info-icon-wrapper">
                      <LibraryDetailsInfoTooltip
                        PopperProps={{
                          disablePortal: true,
                          sx: {
                            marginTop: `-${theme.spacing.sm}px !important`
                          }
                        }}
                        open={slaDescriptionTooltipOpen}
                        disableFocusListener
                        disableHoverListener
                        disableTouchListener
                        onClose={() => setSlaDescriptionTooltipOpen(false)}
                        title={
                          <>
                            <Box className="tooltip-wrapper">
                              <Typography className="tooltip-text">
                                This configuration is for daily SLAs. Weekly and monthly SLAs will be derived from this
                                value.
                              </Typography>
                            </Box>
                          </>
                        }
                      >
                        <IconButton className="tooltip-icon" onClick={handleSlaDescriptionTooltipClick}>
                          {slaDescriptionTooltipOpen ? <LibraryTooltipActiveIcon /> : <LibraryTooltipIcon />}
                        </IconButton>
                      </LibraryDetailsInfoTooltip>
                    </div>
                  </ClickAwayListener>
                </Box>
              )}
            </Box>
          </Box>
          <Box className="benchmarks-wrapper">
            <Box className="benchmarks-row">
              <TextFieldCustomLabel
                name="sla"
                helperText={errors.sla}
                type="number"
                fullWidth
                label="SLA"
                variant="outlined"
                value={values.sla}
                endAdornment={
                  <InputAdornment position="start">
                    {typeof values.sla !== 'number' || values.sla < 0
                      ? ''
                      : getSlaFullUnit(userInput.visualization_param.unit)}
                  </InputAdornment>
                }
                onChange={(e) => {
                  if (e.target.value) {
                    setSaveDisabled(false)
                  }
                  handleChange(e)
                }}
              />
            </Box>
          </Box>
        </>
      )
    }
    if (!userInput.visualization_param.identical_section_values) {
      return (
        <>
          <Box className="benchmarks-title-wrapper">
            <Box className="benchmarks-title">
              <Typography className="benchmark-header">
                {userInput.visualization_param.header}
                <span>*</span>
              </Typography>
              {userInput.visualization_param.sub_header && (
                <Typography className="benchmark-subheader">{userInput.visualization_param.sub_header}</Typography>
              )}
            </Box>
            {multiplicativeValueCpis.includes(manifestData.name) && (
              <Box className="benchmarks-title-tooltip-wrapper">
                <ClickAwayListener onClickAway={() => setSlaDescriptionTooltipOpen(false)}>
                  <div className="info-icon-wrapper">
                    <LibraryDetailsInfoTooltip
                      PopperProps={{
                        disablePortal: true,
                        sx: {
                          marginTop: `-${theme.spacing.sm}px !important`
                        }
                      }}
                      open={slaDescriptionTooltipOpen}
                      disableFocusListener
                      disableHoverListener
                      disableTouchListener
                      onClose={() => setSlaDescriptionTooltipOpen(false)}
                      title={
                        <>
                          <Box className="tooltip-wrapper">
                            <Typography className="tooltip-text">
                              This configuration is for daily SLAs. Weekly and monthly SLAs will be derived from this
                              value.
                            </Typography>
                          </Box>
                        </>
                      }
                    >
                      <IconButton className="tooltip-icon" onClick={handleSlaDescriptionTooltipClick}>
                        {slaDescriptionTooltipOpen ? <LibraryTooltipActiveIcon /> : <LibraryTooltipIcon />}
                      </IconButton>
                    </LibraryDetailsInfoTooltip>
                  </div>
                </ClickAwayListener>
              </Box>
            )}
          </Box>
          {userInput.visualization_param.sections.length > 0 && (
            <>
              <Box className="benchmarks-wrapper">
                {userInput.visualization_param.sections.map((n, i) => (
                  <Box className="benchmarks-row" key={i}>
                    <TextFieldCustomLabel
                      name={n.name}
                      helperText={errors[n.name]}
                      type="number"
                      value={values[n.name]}
                      fullWidth
                      label={`${n.display_name} ${userInput.visualization_param.fields_suffix} `}
                      variant="outlined"
                      endAdornment={
                        <InputAdornment position="end">
                          {typeof values[n.name] !== 'number' || values[n.name] < 0
                            ? ''
                            : getSlaFullUnit(userInput.visualization_param.unit)}
                        </InputAdornment>
                      }
                      onChange={(e) => {
                        if (e.target.value) {
                          setSaveDisabled(false)
                        }
                        handleChange(e)
                      }}
                      showInfoIcon={n.name === 'all'}
                      setEmptyInfoTooltipOpen={setEmptyInfoTooltipOpen}
                      emptyInfoTooltipOpen={emptyInfoTooltipOpen}
                      handleEmptyInfoClick={handleEmptyInfoClick}
                    />
                  </Box>
                ))}
              </Box>
            </>
          )}
        </>
      )
    }

    return null
  }

  const renderFormulaParams = (handleChange: any, errors: any, values: any) => {
    if (!manifestData) return null
    const { formula_params: formulaParams } = manifestData.ui.trend_chart.user_input
    if (formulaParams.length > 0) {
      return formulaParams.map((param, i) => (
        <Fragment key={i}>
          <Box className="benchmarks-title">
            <Typography className="benchmark-header">
              {param.header}
              <span>*</span>
            </Typography>
            {param.sub_header && <Typography className="benchmark-subheader">{param.sub_header}</Typography>}
          </Box>
          <Box className="benchmarks-wrapper">
            {param.sections.length > 0 ? (
              <>
                {param.sections.map((section, idx) => (
                  <Box className="benchmarks-row" key={idx}>
                    <TextFieldCustomLabel
                      name={section.name}
                      helperText={errors[section.name]}
                      type="number"
                      fullWidth
                      value={values[section.name]}
                      label={`${section.display_name} ${param.fields_suffix} `}
                      variant="outlined"
                      endAdornment={
                        <InputAdornment position="start">
                          {!values[section.name] ? '' : getSlaFullUnit(param.unit)}
                        </InputAdornment>
                      }
                      onChange={(e) => {
                        if (e.target.value) {
                          setSaveDisabled(false)
                        }
                        handleChange(e)
                      }}
                      showInfoIcon={section.name === 'all'}
                      setEmptyInfoTooltipOpen={setEmptyInfoTooltipOpen}
                      emptyInfoTooltipOpen={emptyInfoTooltipOpen}
                      handleEmptyInfoClick={handleEmptyInfoClick}
                    />
                  </Box>
                ))}
              </>
            ) : (
              <Box className="benchmarks-row">
                <TextFieldCustomLabel
                  name={param.name}
                  helperText={errors[param.name]}
                  type="number"
                  fullWidth
                  value={values[param.name]}
                  placeholder={`${param.value}` ?? ''}
                  label={param.sub_header}
                  variant="outlined"
                  endAdornment={
                    <InputAdornment position="start">
                      {!values[param.name] ? '' : getSlaFullUnit(param.unit)}
                    </InputAdornment>
                  }
                  onChange={(e) => {
                    if (e.target.value) {
                      setSaveDisabled(false)
                    }
                    handleChange(e)
                  }}
                />
              </Box>
            )}
          </Box>
        </Fragment>
      ))
    }
    return null
  }

  const changeHandler = (e: ChangeEvent<HTMLInputElement>, integration: IntegrationInstance) => {
    const isChecked = e.target.checked

    if (!isChecked && integration.deleted) {
      const callback = integration.is_v2
        ? () => updateIntegration(isChecked, integration.name || '')
        : () => updateIntegration(isChecked, integration.name)

      return dispatch(
        openModal({
          type: ModalType.deselectNotification,
          props: {
            integrationName: integration.name,
            callback
          }
        })
      )
    }

    if (integration.is_v2) {
      updateIntegration(isChecked, integration.instance_name || '')
    } else {
      updateIntegration(isChecked, integration.name)
    }

    const filterItems = cpiSupportedAndUserConfiguredList.filter((item) => !item.deleted)
    if (!isChecked && !integration.deleted && filterItems.length === 1) {
      return dispatch(
        openModal({
          type: ModalType.deleteDeactivation,
          props: {
            integrationName: integration.name,
            cpiSupportedAndUserConfiguredList,
            manifestData: manifestData,
            callback: () => handleDeactivate()
          }
        })
      )
    }
  }

  const updateIntegration = (isChecked: boolean, name: string) => {
    setSaveDisabled(false)
    handleChangeInstance(isChecked, name)
  }

  const handleClickAvailableInstance = (instance: string) => {
    navigate(`/settings/integrations/data-sources/${instance}/add`)
  }

  const getAvailableInstancesText = () => {
    if (!manifestData) return
    const uniqueIntegrationsUserHasConfigured: string[] = []
    userConfiguredIntegrations.forEach((n) => {
      if (!uniqueIntegrationsUserHasConfigured.includes(n.integration_name)) {
        uniqueIntegrationsUserHasConfigured.push(n.integration_name)
      }
    })
    const cpiSupportedNames: string[] = manifestData.supported_integrations.map((n) => n.name)
    let availableWith: string[] = []

    if (!isEqual(cpiSupportedNames, uniqueIntegrationsUserHasConfigured)) {
      const notConfigured = cpiSupportedNames
        .filter((n) => !uniqueIntegrationsUserHasConfigured.includes(n))
        .map((n) => n.replace('_', ' '))
      const configured = cpiSupportedNames
        .filter((n) => uniqueIntegrationsUserHasConfigured.includes(n))
        .map((n) => manifestData.supported_integrations.find((c) => c.name === n)?.display_name || n.replace('_', ' '))
      availableWith = [...notConfigured, ...configured]
    }

    return (
      <Box className="also-available-wrapper">
        <Typography className="also-available-text">
          {availableWith.length > 0 && (
            <>
              Available with{' '}
              {availableWith.map((n, index) => (
                <Fragment key={n}>
                  <span
                    className="also-available-link"
                    onClick={() => handleClickAvailableInstance(n.replace(' ', '_').toLowerCase())}
                  >
                    {n}
                  </span>
                  {index === availableWith.length - 2 ? ' & ' : index < availableWith.length - 1 ? ', ' : ''}
                </Fragment>
              ))}
            </>
          )}
        </Typography>
      </Box>
    )
  }

  const [hasScrollbar, setHasScrollbar] = useState(false)

  const hasScroll = () => {
    if (!wrapperInnerRef.current) return

    const e = wrapperInnerRef.current
    setHasScrollbar(e.scrollWidth < e.getBoundingClientRect().width)
  }

  useEffect(() => {
    hasScroll()

    window.addEventListener('resize', hasScroll)
    return () => window.removeEventListener('resize', hasScroll)
  })

  return (
    <Drawer
      anchor="right"
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      hasScrollbar={hasScrollbar ? 'true' : 'false'}
    >
      <Box className="wrapper-inner" role="presentation" ref={wrapperInnerRef}>
        <Box className="header">
          <Typography className="title">{active ? 'Configuration Settings ' : 'Activating '}</Typography>
          <IconButton className="close-icon" onClick={() => setDrawerOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Formik initialValues={defaultValues} validationSchema={schema} onSubmit={handleSubmit}>
          {({ isSubmitting, isValid, values, handleChange, errors }) => (
            <Form autoComplete="off">
              <Box className="content">
                <Box className="top-section">
                  {!active && fw === 'custom' && customFramework && (
                    <Box className="select-category-wrapper">
                      <Box className="select-category-header">
                        <Typography>Select the category you would like to assign the CPI to</Typography>
                      </Box>
                      <Box className="select-category-container">
                        <Typography className="select-category-text">Category</Typography>
                        <ControlledTrendsChartSelectField
                          options={[...customFramework.data.functions.map((n) => n.title)]}
                          name="cpiCategoryValue"
                          label="Select"
                          onChange={(e) => handleChange(e)}
                          disabled={false}
                          minWidth="121px !important"
                          hideSelected
                        />
                      </Box>
                    </Box>
                  )}

                  <Typography className="text">
                    Select data source(s)
                    <span className="red-required">*</span>
                  </Typography>
                  {getAvailableInstancesText()}
                  {isUpdating ? (
                    <CircularProgress size="16px" color="secondary" />
                  ) : (
                    <Box className="instance-list-wrapper">
                      {active
                        ? cpiSupportedAndUserConfiguredList
                            .filter((integration) => integration.is_v2 === isSnowflake)
                            .map((integration) => (
                              <DataSourceCard
                                key={integration.name}
                                integration={integration}
                                manifestData={manifestData}
                                checked={checked}
                                changeHandler={changeHandler}
                              />
                            ))
                        : notConfiguredInstancesApplicableToCpi.map((integration) => (
                            <DataSourceCard
                              key={integration.name}
                              integration={integration}
                              manifestData={manifestData}
                              checked={checked}
                              changeHandler={changeHandler}
                            />
                          ))}
                    </Box>
                  )}
                </Box>

                {renderVisualizationParam(handleChange, errors, values)}
                {renderFormulaParams(handleChange, errors, values)}

                <Box className="drawer-actions">
                  <Button themevariant="secondary" className="cancel-button" onClick={() => setDrawerOpen(false)}>
                    Cancel
                  </Button>

                  {active ? (
                    <>
                      {hasRole(Role.Demo, authUser) ? (
                        <PermissionTooltip
                          PopperProps={{
                            sx: {
                              marginTop: `-${theme.spacing.sm}px !important`
                            }
                          }}
                          title={
                            <>
                              <Box className="tooltip-wrapper-permissions">
                                <Typography className="tooltip-text">Insufficient permissions</Typography>
                              </Box>
                            </>
                          }
                        >
                          <Button className="save-button" sx={disabledStyles.secondary} disableTouchRipple>
                            Save
                          </Button>
                        </PermissionTooltip>
                      ) : (
                        <Button
                          className="save-button"
                          onClick={() => handleUpdate(values)}
                          disabled={
                            !isValid ||
                            isSubmitting ||
                            saveDisabled ||
                            isUpdating ||
                            !hasPermission(Permission.CPIConfigIntegrationUpdate, authUser)
                          }
                          endIcon={isUpdating && <CircularProgress size="16px" color="secondary" />}
                        >
                          Save
                        </Button>
                      )}

                      {hasRole(Role.Demo, authUser) ? (
                        <PermissionTooltip
                          PopperProps={{
                            sx: {
                              marginTop: `-${theme.spacing.sm}px !important`
                            }
                          }}
                          title={
                            <>
                              <Box className="tooltip-wrapper-permissions">
                                <Typography className="tooltip-text">Insufficient permissions</Typography>
                              </Box>
                            </>
                          }
                        >
                          <Button
                            themevariant="ghost"
                            className="deactivate-button"
                            sx={disabledStyles.secondary}
                            disableTouchRipple
                          >
                            Deactivate CPI
                          </Button>
                        </PermissionTooltip>
                      ) : (
                        <Button
                          themevariant="ghost"
                          type="button"
                          onClick={handleDeactivate}
                          className="deactivate-button"
                        >
                          Deactivate CPI
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      {hasRole(Role.Demo, authUser) ? (
                        <PermissionTooltip
                          PopperProps={{
                            sx: {
                              marginTop: `-${theme.spacing.sm}px !important`
                            }
                          }}
                          title={
                            <>
                              <Box className="tooltip-wrapper-permissions">
                                <Typography className="tooltip-text">Insufficient permissions</Typography>
                              </Box>
                            </>
                          }
                        >
                          <Button
                            className="save-button"
                            sx={{ padding: `${theme.spacing.sm}px 22px !important`, ...disabledStyles.secondary }}
                            disableTouchRipple
                          >
                            Activate CPI
                          </Button>
                        </PermissionTooltip>
                      ) : (
                        <Button
                          className="save-button"
                          type="submit"
                          disabled={
                            !isValid ||
                            isSubmitting ||
                            Boolean(fw === 'custom' && customFramework && !values.cpiCategoryValue)
                          }
                          endIcon={isUpdating && <CircularProgress size="16px" color="secondary" />}
                        >
                          Activate CPI
                        </Button>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Drawer>
  )
}

export default CPILibrarySettingsDrawer
